import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import CategoriasModal from './CategoriasModal';
import ImportacaoModal from './ImportarFichasModal';
import { FaSearch, FaTimes, FaPlus, FaEdit, FaTrash, FaCopy, FaEye, FaInfoCircle, FaSortUp, FaSortDown, FaSort } from 'react-icons/fa';

const FichasTecnicasList = () => {
  const navigate = useNavigate();
  const [fichas, setFichas] = useState([]);
  const [fichasFiltradas, setFichasFiltradas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mostrarModalCategorias, setMostrarModalCategorias] = useState(false);
  const [mostrarModalImportacao, setMostrarModalImportacao] = useState(false);
  
  // Filtros
  const [busca, setBusca] = useState('');
  const [categoriaFiltro, setCategoriaFiltro] = useState('');
  const [tipoFiltro, setTipoFiltro] = useState('todos'); // 'todos', 'produto_final', 'preparacao_base'
  const [categorias, setCategorias] = useState([]);
  
  // Ordenação
  const [ordenacao, setOrdenacao] = useState({ campo: 'nome', direcao: 'asc' });
  
  // UI
  const [viewMode, setViewMode] = useState('table');
  const [isMobile, setIsMobile] = useState(false);

  // Detectar mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth <= 768) {
        setViewMode('cards');
      } else {
        setViewMode('table');
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    carregarFichas();
    carregarCategorias();
  }, []);

  // Aplicar filtros e ordenação em tempo real
  useEffect(() => {
    let resultado = [...fichas];

    // Filtro por busca
    if (busca) {
      resultado = resultado.filter(ficha =>
        ficha.nome.toLowerCase().includes(busca.toLowerCase())
      );
    }

    // Filtro por tipo
    if (tipoFiltro !== 'todos') {
      resultado = resultado.filter(ficha => ficha.tipo_ficha === tipoFiltro);
    }

    // Filtro por categoria
    if (categoriaFiltro) {
      resultado = resultado.filter(ficha => ficha.categoria === categoriaFiltro);
    }

    // Aplicar ordenação
    resultado.sort((a, b) => {
      let valorA = a[ordenacao.campo];
      let valorB = b[ordenacao.campo];

        // Ordenação especial para nome (alfabético primeiro, depois numérico)
        if (ordenacao.campo === 'nome') {
          // Extrair a parte alfabética (até o primeiro número)
          const getParteAlfabetica = (str) => {
            const match = str.match(/^([^\d]+)/);
            return match ? match[0].trim() : str;
          };
          
          const getNumeroPrincipal = (str) => {
            const match = str.match(/\|?\s*(\d+)\s*(fatias?|unidades?)/i);
            return match ? parseInt(match[1]) : 0;
          };
          
          const parteAlfabeticaA = getParteAlfabetica(valorA);
          const parteAlfabeticaB = getParteAlfabetica(valorB);
          
          // Primeiro compara alfabeticamente
          const comparacaoAlfabetica = parteAlfabeticaA.localeCompare(parteAlfabeticaB);
          
          if (comparacaoAlfabetica !== 0) {
            return ordenacao.direcao === 'asc' ? comparacaoAlfabetica : -comparacaoAlfabetica;
          }
          
          // Se a parte alfabética é igual, compara os números
          const numA = getNumeroPrincipal(valorA);
          const numB = getNumeroPrincipal(valorB);
          
          if (numA !== numB) {
            return ordenacao.direcao === 'asc' ? numA - numB : numB - numA;
          }
          
          // Se tudo for igual, comparação alfabética completa
          return ordenacao.direcao === 'asc' 
            ? valorA.localeCompare(valorB)
            : valorB.localeCompare(valorA);
        }

      // Ordenação numérica para outros campos
      if (typeof valorA === 'number' && typeof valorB === 'number') {
        return ordenacao.direcao === 'asc' ? valorA - valorB : valorB - valorA;
      }

      // Ordenação padrão
      if (valorA < valorB) return ordenacao.direcao === 'asc' ? -1 : 1;
      if (valorA > valorB) return ordenacao.direcao === 'asc' ? 1 : -1;
      return 0;
    });

    setFichasFiltradas(resultado);
  }, [busca, tipoFiltro, categoriaFiltro, fichas, ordenacao]);

  const carregarFichas = async () => {
    try {
      setLoading(true);
      const response = await api.get('/fichas/api/fichas-tecnicas');
      
      if (response.data.success) {
        setFichas(response.data.fichas);
      }
    } catch (err) {
      setError('Erro ao carregar fichas técnicas');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const carregarCategorias = async () => {
    try {
      const response = await api.get('/fichas/api/fichas-tecnicas/categorias');
      if (response.data.success) {
        setCategorias(response.data.categorias);
      }
    } catch (err) {
      console.error('Erro ao carregar categorias:', err);
    }
  };

  const limparFiltros = () => {
    setBusca('');
    setCategoriaFiltro('');
    setTipoFiltro('todos');
  };

  const handleOrdenacao = (campo) => {
    setOrdenacao(prev => ({
      campo,
      direcao: prev.campo === campo && prev.direcao === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getIconeOrdenacao = (campo) => {
    if (ordenacao.campo !== campo) return <FaSort className="inline ml-1 text-gray-400" size={12} />;
    return ordenacao.direcao === 'asc' 
      ? <FaSortUp className="inline ml-1 text-blue-600" size={12} />
      : <FaSortDown className="inline ml-1 text-blue-600" size={12} />;
  };

  const handleDuplicar = async (id, nome) => {
    if (!window.confirm(`Duplicar a ficha "${nome}"?`)) return;
  
    try {
      const response = await api.post(`/fichas/api/fichas-tecnicas/${id}/duplicar`);
      
      if (response.data.success) {
        alert('Ficha duplicada com sucesso!');
        carregarFichas();
        navigate(`/fichas-tecnicas/${response.data.ficha_id}/editar`);
      }
    } catch (err) {
      alert('Erro ao duplicar ficha');
      console.error(err);
    }
  };

  const handleDeletar = async (id, nome) => {
    if (!window.confirm(`Tem certeza que deseja excluir a ficha "${nome}"?`)) return;

    try {
      const response = await api.delete(`/fichas/api/fichas-tecnicas/${id}`);
      
      if (response.data.success) {
        alert('Ficha excluída com sucesso!');
        carregarFichas();
      }
    } catch (err) {
      alert('Erro ao excluir ficha');
      console.error(err);
    }
  };

  const formatarMoeda = (valor) => {
    if (!valor && valor !== 0) return 'R$ 0,00';
    return `R$ ${parseFloat(valor).toFixed(2).replace('.', ',')}`;
  };

  const formatarRendimento = (ficha) => {
    const rendimento = ficha.rendimento || 1;
    const unidade = ficha.unidade_rendimento || 'porção';
    
    // Pluralização correta
    let unidadeFinal = unidade;
    if (rendimento !== 1) {
      if (unidade === 'porção') {
        unidadeFinal = 'porções';
      } else if (unidade === 'fatia') {
        unidadeFinal = 'fatias';
      } else if (unidade === 'unidade') {
        unidadeFinal = 'unidades';
      } else {
        unidadeFinal = `${unidade}s`;
      }
    }
    
    // Adicionar peso total se existir
    let resultado = `${rendimento} ${unidadeFinal}`;
    if (ficha.peso_total && ficha.peso_total > 0) {
      const pesoKg = parseFloat(ficha.peso_total) / 1000;
      resultado += ` (${pesoKg.toFixed(3).replace('.', ',')}kg)`;
    }
    
    return resultado;
  };

  // ✅ NOVA FUNÇÃO: Calcular preço por kg
  const calcularPrecoPorKg = (ficha) => {
    // Se não tem peso total, retornar null
    if (!ficha.peso_total || ficha.peso_total === 0) {
      return null;
    }

    const pesoTotalKg = parseFloat(ficha.peso_total) / 1000; // Converter g para kg
    const custoTotal = parseFloat(ficha.custo_total) || 0;

    if (pesoTotalKg === 0) return null;

    return custoTotal / pesoTotalKg;
  };

  // Separar fichas por tipo
  const fichasProdutoFinal = fichasFiltradas.filter(f => f.tipo_ficha === 'produto_final');
  const fichasPreparacaoBase = fichasFiltradas.filter(f => f.tipo_ficha === 'preparacao_base');

  // Componente de Card para mobile
  const FichaCard = ({ ficha }) => {
    const precoPorKg = calcularPrecoPorKg(ficha);
    
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-3 shadow-sm">
        <div className="flex justify-between items-start mb-2">
          <div className="flex-1">
            <h3 className="font-semibold text-gray-800 text-base">{ficha.nome}</h3>
            {ficha.categoria && (
              <span className="inline-block bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs mt-1">
                {ficha.categoria}
              </span>
            )}
          </div>
          <span className={`text-xs px-2 py-1 rounded ${
            ficha.tipo_ficha === 'produto_final' 
              ? 'bg-green-100 text-green-700' 
              : 'bg-yellow-100 text-yellow-700'
          }`}>
            {ficha.tipo_ficha === 'produto_final' ? '🎂 Produto' : '🧈 Base'}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-2 text-xs mb-3">
          <div>
            <span className="text-gray-500">Rendimento:</span>
            <span className="ml-1 text-gray-700">{formatarRendimento(ficha)}</span>
          </div>
          <div>
            <span className="text-gray-500">Custo Total:</span>
            <span className="ml-1 text-gray-700 font-semibold">{formatarMoeda(ficha.custo_total)}</span>
          </div>
          <div>
            <span className="text-gray-500">Custo/unid:</span>
            <span className="ml-1 text-blue-600 font-semibold">{formatarMoeda(ficha.custo_unitario)}</span>
          </div>
          {precoPorKg && (
            <div>
              <span className="text-gray-500">Preço/kg:</span>
              <span className="ml-1 text-purple-600 font-semibold">{formatarMoeda(precoPorKg)}</span>
            </div>
          )}
        </div>

        <div className="flex gap-1">
          <button 
            className="flex-1 bg-blue-500 text-white p-2 rounded text-xs hover:bg-blue-600"
            onClick={() => navigate(`/fichas-tecnicas/${ficha.id}`)}
          >
            <FaEye className="inline" /> Ver
          </button>
          <button 
            className="flex-1 bg-gray-500 text-white p-2 rounded text-xs hover:bg-gray-600"
            onClick={() => navigate(`/fichas-tecnicas/${ficha.id}/editar`)}
          >
            <FaEdit className="inline" /> Editar
          </button>
          <button 
            className="bg-purple-500 text-white p-2 rounded hover:bg-purple-600"
            onClick={() => handleDuplicar(ficha.id, ficha.nome)}
          >
            <FaCopy size={14} />
          </button>
          <button 
            className="bg-red-500 text-white p-2 rounded hover:bg-red-600"
            onClick={() => handleDeletar(ficha.id, ficha.nome)}
          >
            <FaTrash size={14} />
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto mt-10 p-4">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto mt-10 bg-white p-4 md:p-8 rounded-lg shadow-lg">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <h2 className="text-2xl font-semibold text-gray-700">
          Fichas Técnicas
        </h2>
        
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setMostrarModalCategorias(true)}
            className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition text-sm"
          >
            Gerenciar Categorias
          </button>
          <button
            onClick={() => setMostrarModalImportacao(true)}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition text-sm"
          >
            Importar Excel
          </button>
          <button
            onClick={() => navigate('/fichas-tecnicas/nova')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition flex items-center gap-2 text-sm"
          >
            <FaPlus />
            Nova Ficha
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="relative">
          <FaSearch className="absolute left-3 top-3 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por nome..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {busca && (
            <button
              onClick={() => setBusca('')}
              className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
            >
              <FaTimes />
            </button>
          )}
        </div>

        <select
          value={tipoFiltro}
          onChange={(e) => setTipoFiltro(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="todos">📋 Todos os tipos</option>
          <option value="produto_final">🎂 Produtos Finais</option>
          <option value="preparacao_base">🧈 Preparações Base</option>
        </select>

        <select
          value={categoriaFiltro}
          onChange={(e) => setCategoriaFiltro(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="">🏷️ Todas as categorias</option>
          {categorias.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
      </div>

      {/* Indicador de filtros ativos */}
      {(busca || categoriaFiltro || tipoFiltro !== 'todos') && (
        <div className="mb-4 flex items-center gap-2 flex-wrap">
          <span className="text-sm text-gray-600">Filtros ativos:</span>
          {busca && (
            <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm flex items-center gap-2">
              Busca: "{busca}"
              <FaTimes className="cursor-pointer hover:text-blue-900" onClick={() => setBusca('')} />
            </span>
          )}
          {tipoFiltro !== 'todos' && (
            <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm flex items-center gap-2">
              {tipoFiltro === 'produto_final' ? 'Produtos Finais' : 'Preparações Base'}
              <FaTimes className="cursor-pointer hover:text-green-900" onClick={() => setTipoFiltro('todos')} />
            </span>
          )}
          {categoriaFiltro && (
            <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm flex items-center gap-2">
              {categoriaFiltro}
              <FaTimes className="cursor-pointer hover:text-purple-900" onClick={() => setCategoriaFiltro('')} />
            </span>
          )}
          <button
            onClick={limparFiltros}
            className="text-sm text-red-600 hover:text-red-700 underline"
          >
            Limpar todos
          </button>
        </div>
      )}

      {/* Toggle de visualização (apenas desktop) */}
      {!isMobile && (
        <div className="mb-4 flex gap-2">
          <button
            onClick={() => setViewMode('table')}
            className={`px-4 py-2 rounded-lg text-sm ${
              viewMode === 'table' 
                ? 'bg-blue-500 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Tabela
          </button>
          <button
            onClick={() => setViewMode('cards')}
            className={`px-4 py-2 rounded-lg text-sm ${
              viewMode === 'cards' 
                ? 'bg-blue-500 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Cards
          </button>
        </div>
      )}

      {/* Conteúdo */}
      {fichasFiltradas.length === 0 ? (
        <div className="text-center py-12">
          <FaInfoCircle className="mx-auto text-6xl text-gray-300 mb-4" />
          <p className="text-gray-500 text-lg">Nenhuma ficha técnica encontrada</p>
          <p className="text-gray-400 text-sm mt-2">
            {busca || categoriaFiltro || tipoFiltro !== 'todos'
              ? 'Tente ajustar os filtros ou limpar a busca'
              : 'Clique em "Nova Ficha" para começar'
            }
          </p>
        </div>
      ) : (
        <>
          {viewMode === 'table' ? (
            <>
              {/* Tabela de Produtos Finais */}
              {(tipoFiltro === 'todos' || tipoFiltro === 'produto_final') && fichasProdutoFinal.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm">
                      🎂 Produtos Finais ({fichasProdutoFinal.length})
                    </span>
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-300 text-sm">
                      <thead>
                        <tr className="bg-gray-100">
                          <th 
                            className="border border-gray-300 px-2 py-2 text-left font-medium text-gray-700 cursor-pointer hover:bg-gray-200"
                            style={{ minWidth: '180px' }}
                            onClick={() => handleOrdenacao('nome')}
                          >
                            Nome {getIconeOrdenacao('nome')}
                          </th>
                          <th 
                            className="border border-gray-300 px-2 py-2 text-center font-medium text-gray-700"
                            style={{ width: '110px' }}
                          >
                            Categoria
                          </th>
                          <th 
                            className="border border-gray-300 px-2 py-2 text-center font-medium text-gray-700"
                            style={{ width: '120px' }}
                          >
                            Rendimento
                          </th>
                          <th 
                            className="border border-gray-300 px-2 py-2 text-right font-medium text-gray-700 cursor-pointer hover:bg-gray-200"
                            style={{ width: '95px' }}
                            onClick={() => handleOrdenacao('custo_total')}
                          >
                            Custo Total {getIconeOrdenacao('custo_total')}
                          </th>
                          <th 
                            className="border border-gray-300 px-2 py-2 text-right font-medium text-gray-700 cursor-pointer hover:bg-gray-200"
                            style={{ width: '90px' }}
                            onClick={() => handleOrdenacao('custo_unitario')}
                          >
                            Custo/unid {getIconeOrdenacao('custo_unitario')}
                          </th>
                          <th 
                            className="border border-gray-300 px-2 py-2 text-right font-medium text-gray-700"
                            style={{ width: '85px' }}
                          >
                            Preço/kg
                          </th>
                          <th 
                            className="border border-gray-300 px-2 py-2 text-right font-medium text-gray-700 cursor-pointer hover:bg-gray-200"
                            style={{ width: '100px' }}
                            onClick={() => handleOrdenacao('preco_venda_sugerido')}
                          >
                            Preço Venda {getIconeOrdenacao('preco_venda_sugerido')}
                          </th>
                          <th 
                            className="border border-gray-300 px-2 py-2 text-center font-medium text-gray-700 cursor-pointer hover:bg-gray-200"
                            style={{ width: '70px' }}
                            onClick={() => handleOrdenacao('cmv_percentual')}
                          >
                            CMV {getIconeOrdenacao('cmv_percentual')}
                          </th>
                          <th 
                            className="border border-gray-300 px-2 py-2 text-center font-medium text-gray-700"
                            style={{ width: '140px' }}
                          >
                            Ações
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {fichasProdutoFinal.map(ficha => {
                          const precoPorKg = calcularPrecoPorKg(ficha);
                          
                          return (
                            <tr key={ficha.id} className="border-gray-300 hover:bg-gray-50 transition-colors">
                              <td className="border border-gray-300 px-2 py-1.5 whitespace-nowrap overflow-hidden text-ellipsis" style={{ maxWidth: '180px' }}>
                                <strong>{ficha.nome}</strong>
                                {ficha.observacoes && (
                                  <small className="block text-gray-500 text-xs truncate">
                                    {ficha.observacoes}
                                  </small>
                                )}
                              </td>
                              <td className="border border-gray-300 px-2 py-1.5 text-center">
                                {ficha.categoria && (
                                  <span className="inline-block bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs">
                                    {ficha.categoria}
                                  </span>
                                )}
                              </td>
                              <td className="border border-gray-300 px-2 py-1.5 text-center text-gray-600 text-xs">
                                {formatarRendimento(ficha)}
                              </td>
                              <td className="border border-gray-300 px-2 py-1.5 text-right font-semibold text-gray-700">
                                {formatarMoeda(ficha.custo_total)}
                              </td>
                              <td className="border border-gray-300 px-2 py-1.5 text-right font-semibold text-blue-600">
                                {formatarMoeda(ficha.custo_unitario)}
                              </td>
                              <td className="border border-gray-300 px-2 py-1.5 text-right font-semibold text-purple-600">
                                {precoPorKg ? formatarMoeda(precoPorKg) : '-'}
                              </td>
                              <td className="border border-gray-300 px-2 py-1.5 text-right font-semibold text-green-600">
                                {formatarMoeda(ficha.preco_venda_sugerido)}
                              </td>
                              <td className="border border-gray-300 px-2 py-1.5 text-center">
                                {ficha.cmv_percentual ? (
                                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                                    ficha.cmv_percentual <= 31 ? 'bg-green-100 text-green-700' :
                                    ficha.cmv_percentual <= 40 ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-red-100 text-red-700'
                                  }`}>
                                    {ficha.cmv_percentual.toFixed(1)}%
                                  </span>
                                ) : '-'}
                              </td>
                              <td className="border border-gray-300 px-1 py-1">
                                <div className="flex gap-1 items-center justify-center">
                                  <button
                                    className="bg-blue-500 text-white p-1.5 rounded hover:bg-blue-600 transition-colors"
                                    onClick={() => navigate(`/fichas-tecnicas/${ficha.id}`)}
                                    title="Ver"
                                  >
                                    <FaEye size={12} />
                                  </button>
                                  <button
                                    className="bg-gray-500 text-white p-1.5 rounded hover:bg-gray-600 transition-colors"
                                    onClick={() => navigate(`/fichas-tecnicas/${ficha.id}/editar`)}
                                    title="Editar"
                                  >
                                    <FaEdit size={12} />
                                  </button>
                                  <button
                                    className="bg-purple-500 text-white p-1.5 rounded hover:bg-purple-600 transition-colors"
                                    onClick={() => handleDuplicar(ficha.id, ficha.nome)}
                                    title="Duplicar"
                                  >
                                    <FaCopy size={12} />
                                  </button>
                                  <button
                                    className="bg-red-500 text-white p-1.5 rounded hover:bg-red-600 transition-colors"
                                    onClick={() => handleDeletar(ficha.id, ficha.nome)}
                                    title="Excluir"
                                  >
                                    <FaTrash size={12} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Tabela de Preparações Base */}
              {(tipoFiltro === 'todos' || tipoFiltro === 'preparacao_base') && fichasPreparacaoBase.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-sm">
                      🧈 Preparações Base ({fichasPreparacaoBase.length})
                    </span>
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-300 text-sm">
                      <thead>
                        <tr className="bg-gray-100">
                          <th 
                            className="border border-gray-300 px-2 py-2 text-left font-medium text-gray-700 cursor-pointer hover:bg-gray-200"
                            style={{ minWidth: '150px' }}
                            onClick={() => handleOrdenacao('nome')}
                          >
                            Nome {getIconeOrdenacao('nome')}
                          </th>
                          <th 
                            className="border border-gray-300 px-2 py-2 text-center font-medium text-gray-700"
                            style={{ width: '120px' }}
                          >
                            Categoria
                          </th>
                          <th 
                            className="border border-gray-300 px-2 py-2 text-center font-medium text-gray-700"
                            style={{ width: '130px' }}
                          >
                            Rendimento
                          </th>
                          <th 
                            className="border border-gray-300 px-2 py-2 text-right font-medium text-gray-700 cursor-pointer hover:bg-gray-200"
                            style={{ width: '100px' }}
                            onClick={() => handleOrdenacao('custo_total')}
                          >
                            Custo Total {getIconeOrdenacao('custo_total')}
                          </th>
                          <th 
                            className="border border-gray-300 px-2 py-2 text-right font-medium text-gray-700 cursor-pointer hover:bg-gray-200"
                            style={{ width: '100px' }}
                            onClick={() => handleOrdenacao('custo_unitario')}
                          >
                            Custo/porção {getIconeOrdenacao('custo_unitario')}
                          </th>
                          <th 
                            className="border border-gray-300 px-2 py-2 text-right font-medium text-gray-700"
                            style={{ width: '90px' }}
                          >
                            Preço/kg
                          </th>
                          <th 
                            className="border border-gray-300 px-2 py-2 text-center font-medium text-gray-700"
                            style={{ width: '140px' }}
                          >
                            Ações
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {fichasPreparacaoBase.map(ficha => {
                          const precoPorKg = calcularPrecoPorKg(ficha);
                          
                          return (
                            <tr key={ficha.id} className="border-gray-300 hover:bg-gray-50 transition-colors">
                              <td className="border border-gray-300 px-2 py-1.5 whitespace-nowrap overflow-hidden text-ellipsis" style={{ maxWidth: '200px' }}>
                                <strong>{ficha.nome}</strong>
                                {ficha.observacoes && (
                                  <small className="block text-gray-500 text-xs truncate">
                                    {ficha.observacoes}
                                  </small>
                                )}
                              </td>
                              <td className="border border-gray-300 px-2 py-1.5 text-center">
                                {ficha.categoria && (
                                  <span className="inline-block bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs">
                                    {ficha.categoria}
                                  </span>
                                )}
                              </td>
                              <td className="border border-gray-300 px-2 py-1.5 text-center text-gray-600 text-xs">
                                {formatarRendimento(ficha)}
                              </td>
                              <td className="border border-gray-300 px-2 py-1.5 text-right font-semibold text-gray-700">
                                {formatarMoeda(ficha.custo_total)}
                              </td>
                              <td className="border border-gray-300 px-2 py-1.5 text-right font-semibold text-blue-600">
                                {formatarMoeda(ficha.custo_unitario)}
                              </td>
                              <td className="border border-gray-300 px-2 py-1.5 text-right font-semibold text-purple-600">
                                {precoPorKg ? formatarMoeda(precoPorKg) : '-'}
                              </td>
                              <td className="border border-gray-300 px-1 py-1">
                                <div className="flex gap-1 items-center justify-center">
                                  <button
                                    className="bg-blue-500 text-white p-1.5 rounded hover:bg-blue-600 transition-colors"
                                    onClick={() => navigate(`/fichas-tecnicas/${ficha.id}`)}
                                    title="Ver"
                                  >
                                    <FaEye size={12} />
                                  </button>
                                  <button
                                    className="bg-gray-500 text-white p-1.5 rounded hover:bg-gray-600 transition-colors"
                                    onClick={() => navigate(`/fichas-tecnicas/${ficha.id}/editar`)}
                                    title="Editar"
                                  >
                                    <FaEdit size={12} />
                                  </button>
                                  <button
                                    className="bg-purple-500 text-white p-1.5 rounded hover:bg-purple-600 transition-colors"
                                    onClick={() => handleDuplicar(ficha.id, ficha.nome)}
                                    title="Duplicar"
                                  >
                                    <FaCopy size={12} />
                                  </button>
                                  <button
                                    className="bg-red-500 text-white p-1.5 rounded hover:bg-red-600 transition-colors"
                                    onClick={() => handleDeletar(ficha.id, ficha.nome)}
                                    title="Excluir"
                                  >
                                    <FaTrash size={12} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="space-y-3">
              {fichasFiltradas.map(ficha => (
                <FichaCard key={ficha.id} ficha={ficha} />
              ))}
            </div>
          )}

          {/* Rodapé com totais */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex flex-col md:flex-row justify-between items-center gap-3">
              <span className="font-medium text-gray-700">
                Total: {fichasFiltradas.length} {fichasFiltradas.length === 1 ? 'ficha' : 'fichas'}
              </span>
              <div className="flex gap-3">
                <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium">
                  {fichasProdutoFinal.length} Produtos Finais
                </span>
                <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-sm font-medium">
                  {fichasPreparacaoBase.length} Preparações Base
                </span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Modal de Categorias */}
      {mostrarModalCategorias && (
        <CategoriasModal
          fecharModal={() => setMostrarModalCategorias(false)}
          recarregar={() => {
            carregarCategorias();
            carregarFichas();
          }}
        />
      )}

      {/* Modal de Importação */}
      {mostrarModalImportacao && (
        <ImportacaoModal
          fecharModal={() => setMostrarModalImportacao(false)}
          recarregar={() => {
            carregarFichas();
          }}
        />
      )}
    </div>
  );
};

export default FichasTecnicasList;